import styles from "./styles.scss";
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { flip, offset, useFloating } from "@floating-ui/react";
import clsx from "clsx";
import { Chevron, Cross } from "@/assets/icons";
import { useClickOutside } from "@/hooks";
import { DropdownProps } from "./types";

export const Dropdown = <T extends string = string, J extends string = string>({
  id: dropdownId,
  placeholder,
  selectedOption,
  options,
  onChange,
  onClear,
  label,
  disabled,
  triggerClassName,
  containerClassName,
  menuClassName,
  itemClassName,
  onCloseMenu,
  isNeedToHighlightSelectedItem,
  mandatory,
  searchable,
  ...props
}: DropdownProps<T, J>) => {
  const containerRef = useRef(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [disableFilter, setDisableFilter] = useState(false);
  const hasSelection = !!selectedOption;

  useEffect(() => {
    if (!isOpen && onCloseMenu) {
      onCloseMenu(dropdownId);
    }
  }, [dropdownId, isOpen, onCloseMenu]);

  useEffect(() => {
    const value = selectedOption?.DisplayName ?? selectedOption?.Value;
    setSearchValue(value);
  }, [selectedOption]);

  const {
    x,
    y,
    strategy,
    update,
    refs: { reference: referenceRef, setReference, setFloating },
  } = useFloating({
    placement: "bottom",
    middleware: [flip(), offset(4)],
  });

  const triggerRef = referenceRef as MutableRefObject<HTMLElement>;

  const handleClose = useCallback(() => {
    setIsOpen(false);

    if (searchable) {
      const value = selectedOption?.DisplayName ?? selectedOption?.Value ?? "";
      setSearchValue(value);
    }
  }, [selectedOption, searchable]);

  useClickOutside(containerRef, handleClose);

  const handleTriggerClick = useCallback(() => {
    update();
    setIsOpen(prev => {
      const next = !prev;

      if (next && searchable) {
        setDisableFilter(true);
      }

      return next;
    });
  }, [update, searchable]);

  return (
    <div
      ref={containerRef}
      className={clsx(styles.container, containerClassName)}
    >
      {label && (
        <div className={styles.label}>
          {label}
          {mandatory && <span className={styles.asterix}> *</span>}
        </div>
      )}

      {searchable ? (
        <div className={styles.inputContainer}>
          <input
            ref={inputRef}
            type="text"
            value={searchValue}
            placeholder={
              hasSelection
                ? selectedOption?.DisplayName ?? selectedOption?.Value ?? ""
                : placeholder
            }
            onChange={e => {
              const next = e.target.value;
              setSearchValue(next);

              if (!isOpen) {
                setIsOpen(true);
              }

              if (disableFilter) {
                setDisableFilter(false);
              }

              if (next === "" && hasSelection) {
                if (onClear) {
                  onClear(dropdownId);
                }
              }
            }}
            className={clsx(
              styles.trigger,
              !hasSelection && styles.placeholderShown,
              triggerClassName,
            )}
            onClick={handleTriggerClick}
            disabled={disabled}
            {...props}
          />

          <div className={clsx(styles.searchInputIconsContainer)}>
            {hasSelection || (searchValue && searchValue.trim() !== "") ? (
              <Cross
                width={24}
                height={24}
                className={styles.searchInputCancelButton}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSearchValue("");

                  if (isOpen) {
                    setDisableFilter(false);
                  } else {
                    setDisableFilter(true);
                  }

                  if (onClear) {
                    onClear(dropdownId);
                  }

                  requestAnimationFrame(() => {
                    inputRef.current?.focus();
                  });
                }}
              />
            ) : null}
            <Chevron width={24} height={24} />
          </div>
        </div>
      ) : (
        <button
          ref={setReference}
          type="button"
          onClick={handleTriggerClick}
          className={clsx(
            styles.trigger,
            !selectedOption && styles.placeholderShown,
            triggerClassName,
          )}
          disabled={disabled}
          {...props}
        >
          <span
            className={clsx(styles.text, !selectedOption && styles.placeholder)}
          >
            {hasSelection
              ? selectedOption?.DisplayName ?? selectedOption?.Value
              : placeholder}
          </span>
          <Chevron width={24} height={24} className={styles.chevron} />
        </button>
      )}
      {isOpen && (
        <ul
          ref={setFloating}
          style={{
            position: strategy,
            top: y ?? "",
            left: x ?? "",
            width: triggerRef.current?.offsetWidth,
          }}
          className={clsx(styles.menu, menuClassName)}
        >
          {(() => {
            const filteredOptions = options.filter(option => {
              if (!searchable) {
                return true;
              }

              if (disableFilter) {
                return true;
              }

              if (!searchValue) {
                return true;
              }

              const text = (option.DisplayName ?? option.Value ?? "")
                .toString()
                .toLowerCase();

              return text.includes(searchValue.toLowerCase());
            });

            if (!filteredOptions.length) {
              return (
                <li className={clsx(styles.menuItem, itemClassName)}>
                  There are no options
                </li>
              );
            }

            return filteredOptions.map(option => {
              const { Id, Value, DisplayName } = option;

              const handleItemClick = () => {
                setIsOpen(false);
                setDisableFilter(false);
                onChange(option, dropdownId);
              };

              return (
                <li
                  key={`${Id}`}
                  className={clsx(
                    styles.menuItem,
                    itemClassName,
                    isNeedToHighlightSelectedItem &&
                      Id === selectedOption?.Id &&
                      styles.selectedHighlightedItem,
                  )}
                  onClick={handleItemClick}
                >
                  {DisplayName ?? Value}
                </li>
              );
            });
          })()}
        </ul>
      )}
    </div>
  );
};
